import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { isEmpty, concat } from 'lodash';
import { useThrottleFn } from 'ahooks';
import {
  MESSAGES_DELETE_SUCCESS,
  MESSAGES_NEW,
  ROOMS_ONLINE_RESPONSE,
  MESSAGES_ACTIVE,
} from 'components/storefront/Room/ducks';
import { createAction } from 'dorothy/utils';
import {
  connectMqtt,
  disconnectMqtt,
  publishMessage,
  decompressMessage,
  decompressMessageToBuffer,
  reconstructMessage,
} from './mqttService';
import { decodeMessage } from 'utils/protobuf';

const createTopicPatterns = roomId => ({
  MESSAGE: `chat/room/${roomId}/message`,
  MESSAGE_DELETE: `chat/room/${roomId}/message_delete`,
});

const MAX_MESSAGES = 1000;

export const useChatBox = ({ room, user }, dispatch) => {
  const [connected, setConnected] = useState(false);
  const messageListRef = useRef([]);
  const hasConnectedRef = useRef(false); // guard tránh connect trùng
  const { run } = useThrottleFn(
    data => {
      dispatch(createAction(MESSAGES_NEW, { data }));
      messageListRef.current = [];
    },
    { wait: 1000 },
  );

  // --- NEW: cleanup throttler to avoid timer/closure leaks ---
  useEffect(() => {
    return () => {
      if (typeof run?.cancel === 'function') {
        try { run.cancel(); } catch {}
      }
    };
  }, []); // mount once

  const addMessage = useCallback((message) => {
    const isDuplicate = messageListRef.current.some(
      existingMessage => existingMessage.id === message.id
    );
    if (isDuplicate) return;

    messageListRef.current = [
      ...messageListRef.current.slice(-MAX_MESSAGES + 1),
      message
    ];
  }, []);

  const topicPatterns = useMemo(() => {
    if (!isEmpty(room?.id)) {
      return createTopicPatterns(room.id);
    }

    return null;
  }, [room?.id]);

  // Callback process message received
  const handleMessageFromMqtt = useCallback(
    (topic, decompressed) => {
      if (!topicPatterns) return;
      const __length = decompressed.length || 0;
      if(__length < 35) return;
      try {
        // Message topic
        if (topic === topicPatterns.MESSAGE) {
          const decodedMessage = decodeMessage(decompressed);
          const message = reconstructMessage(decodedMessage.values);
          if (!isEmpty(user?.id) && user?.id === message?.user?.id) {
            dispatch(createAction(MESSAGES_ACTIVE, message));
            return;
          }

          addMessage(message);
          run(messageListRef.current);
        }

        // Room online topic
        if (topic === topicPatterns.ROOM_ONLINE) {
          dispatch(createAction(ROOMS_ONLINE_RESPONSE, decompressed));
        }

        // Message delete topic
        if (topic === topicPatterns.MESSAGE_DELETE) {
          const { id } = JSON.parse(decompressed);
          if (id) {
            dispatch(createAction(MESSAGES_DELETE_SUCCESS, id));
          }
        }
      } catch (error) {
        console.error('Error processing message:', error);
      }
    },
    [topicPatterns, user?.id, dispatch, run, addMessage],
  );

  const handleMessageFromParentSite = useCallback(
    event => {
      const { type, payload } = event.data;
      if (type === 'chat-iframe' && topicPatterns) {
        // Subscribe all topics of the room
        const topics = Object.values(topicPatterns);
        // Connect and subscribe
        connectMqtt(handleMessageFromMqtt, topics);
        setConnected(true);

        // Cleanup when unmount
        return () => {
          disconnectMqtt();
          setConnected(false);
        };
      }
      if (!payload || !topicPatterns) return;

      let parsedMessage = decompressMessage(payload);

      if (type === 'CHAT_MESSAGE') {
        parsedMessage = decompressMessageToBuffer(payload);
        handleMessageFromMqtt(topicPatterns.MESSAGE, parsedMessage);
      }

      if (type === 'CHAT_MESSAGE_DELETE') {
        handleMessageFromMqtt(topicPatterns.MESSAGE_DELETE, parsedMessage);
      }
    },
    [topicPatterns, handleMessageFromMqtt],
  );

  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (!isInIframe) return;
    window.addEventListener('message', handleMessageFromParentSite);

    return () => {
      window.removeEventListener('message', handleMessageFromParentSite);
    };
  }, [handleMessageFromParentSite]); // [topicPatterns, handleMessageFromParentSite]
  
  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (isInIframe || !topicPatterns) return;

    // Subscribe all topics of the room
    const topics = Object.values(topicPatterns);

    // Connect and subscribe
    // connectMqtt(handleMessageFromMqtt, topics);
    // setConnected(true);
    if (!hasConnectedRef.current) {
      connectMqtt(handleMessageFromMqtt, topics);
      hasConnectedRef.current = true;
      setConnected(true);
    }

    return () => {
      // disconnectMqtt();
      // setConnected(false);
      /*
      try {
        disconnectMqtt();
      } finally {
        hasConnectedRef.current = false;
        setConnected(false);
      }
      */
           try {
       messageListRef.current = []; // 🆕 FIX: clear messages khi unmount để tránh giữ ref
       disconnectMqtt();
     } finally {
       hasConnectedRef.current = false;
       setConnected(false);
     }
    };
  }, [topicPatterns]); //[topicPatterns, handleMessageFromMqtt, user?.id]

  // Function send message
  const send = useCallback(
    message => {
      if (!connected || !room?.id) return;

      publishMessage(topicPatterns.MESSAGE, {
        ...message,
        room: room.id,
      });
    },
    [topicPatterns, connected, room?.id],
  );

  return { connected, send };
};

export default useChatBox;
